import { useContext } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useColorMode,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import { logEvent } from 'firebase/analytics';
import type { User } from '@/models';
import { hasPasswordProvider } from '@/helpers';
import { FirebaseContext } from '@/session/FirebaseContext';
import { siteNavAccountButtonProps } from './siteNavLayout';

/** Desktop account menu (hidden when nav is compact; account actions live in the drawer). */
export function SiteNavAccountMenu(props: {
  userData: User;
  visible: boolean;
  openEditName: () => void;
  openDeleteAccount: () => void;
  openSetPassword: () => void;
  openChangePassword: () => void;
}) {
  const { admin, name: userName } = props.userData;
  const { colorMode } = useColorMode();
  const ctx = useContext(FirebaseContext);
  const navigate = useNavigate();

  const signOut = async () => {
    await ctx.auth.signOut();
    logEvent(ctx.analytics, 'log_out');
    navigate('/rules', { replace: true });
  };

  if (!props.visible) return null;

  return (
    <Box>
      <Menu>
        <MenuButton
          as={Button}
          {...siteNavAccountButtonProps}
          variant='ghost'
          rightIcon={<ChevronDownIcon boxSize={3} />}
        >
          <Text mr={1} fontSize={siteNavAccountButtonProps.fontSize} noOfLines={1} maxW='12rem'>
            {userName || 'Account'}
          </Text>
        </MenuButton>
        <MenuList>
          {admin ? (
            <MenuItem fontSize={14} onClick={() => navigate('/admin', { replace: true })}>
              Admin Console
            </MenuItem>
          ) : null}
          <MenuItem fontSize={14} onClick={props.openEditName}>
            Edit Name
          </MenuItem>
          {ctx.user && !hasPasswordProvider(ctx.user) ? (
            <MenuItem fontSize={14} onClick={props.openSetPassword}>
              Set password
            </MenuItem>
          ) : null}
          {ctx.user && hasPasswordProvider(ctx.user) ? (
            <MenuItem fontSize={14} onClick={props.openChangePassword}>
              Change password
            </MenuItem>
          ) : null}
          <MenuItem fontSize={14} onClick={() => void signOut()}>
            Sign Out
          </MenuItem>
          <MenuItem
            textColor={colorMode === 'light' ? 'red.500' : 'red.300'}
            fontSize={14}
            onClick={props.openDeleteAccount}
          >
            Delete Account
          </MenuItem>
        </MenuList>
      </Menu>
    </Box>
  );
}
